import { Field } from '@siliconx/shared/ui/Field';

/**
 * The portal's ONE date input (forward-availability epic). A labeled native
 * `<input type="date">` — native over a picker lib on purpose — with min/max
 * cap enforcement and inline helper/error text. Every date field in the
 * customer portal goes through this so cap rules can't drift per-form.
 */
export function DateField({
  label,
  value,
  onChange,
  min,
  max,
  required,
  helper,
  error,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  /** yyyy-mm-dd inclusive lower bound. */
  min?: string;
  /** yyyy-mm-dd inclusive upper bound. */
  max?: string;
  required?: boolean;
  helper?: string;
  error?: string | null;
  disabled?: boolean;
}) {
  return (
    <Field label={required ? `${label} *` : label} hint={helper ?? undefined} error={error ?? undefined}>
      <input
        type="date"
        value={value}
        min={min}
        max={max}
        required={required}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? true : undefined}
        className={`mt-1 w-full rounded border px-3 py-1.5 text-sm text-zinc-800 focus:outline-none ${
          error
            ? 'border-red-400 focus:border-red-500'
            : 'border-zinc-300 focus:border-blue-500'
        } disabled:bg-zinc-50 disabled:text-zinc-400`}
      />
    </Field>
  );
}

/**
 * A From/By pair of DateFields for a delivery-window range. `from <= by` is
 * validated here (the error renders under the From field) so every range
 * picker enforces it identically.
 */
export function DateRangeField({
  fromLabel = 'From',
  byLabel = 'By',
  from,
  by,
  onFromChange,
  onByChange,
  min,
  max,
  helper,
}: {
  fromLabel?: string;
  byLabel?: string;
  from: string;
  by: string;
  onFromChange: (next: string) => void;
  onByChange: (next: string) => void;
  min?: string;
  max?: string;
  helper?: string;
}) {
  const orderError = from && by && from > by ? '"From" must be on or before "By".' : null;
  return (
    <div>
      <div className="grid grid-cols-2 gap-3">
        <DateField
          label={fromLabel}
          value={from}
          onChange={onFromChange}
          min={min}
          max={by || max}
          error={orderError}
        />
        <DateField label={byLabel} value={by} onChange={onByChange} min={from || min} max={max} />
      </div>
      {helper && !orderError && <p className="mt-1 text-xs text-zinc-400">{helper}</p>}
    </div>
  );
}
